import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FiPlus, FiEdit, FiTrash2, FiMapPin, FiCalendar, FiDollarSign, FiUsers, FiChevronLeft, FiChevronRight, FiPower, FiXCircle, FiGift } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ViagemModal from '../components/ViagemModal';
import PromocaoModal from '../components/PromocaoModal';
import { formatarData } from '../utils/dateUtils';
import { getImageUrl, getYouTubeEmbedUrl } from '../utils/imageUtils';

// Componente de carrossel de imagens
const ImageCarousel = ({ images, titulo, videoEmbedUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (images.length === 0) {
    return videoEmbedUrl ? (
      <iframe
        src={videoEmbedUrl}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={titulo}
      ></iframe>
    ) : (
      <div className="h-full flex items-center justify-center bg-gray-100">
        <p className="text-gray-400 text-sm">Sem imagem</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full group">
      {images.map((imageUrl, index) => (
        <img
          key={index}
          src={imageUrl}
          alt={`${titulo} - Imagem ${index + 1}`}
          className={`w-full h-full object-contain absolute inset-0 transition-opacity duration-300 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ))}
      
      {/* Indicadores de posição */}
      {images.length > 1 && (
        <>
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? 'w-6 bg-white' : 'w-2 bg-white bg-opacity-50'
                }`}
                aria-label={`Ir para imagem ${index + 1}`}
              />
            ))}
          </div>
          
          {/* Botões de navegação */}
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70 z-10"
            aria-label="Imagem anterior"
          >
            <FiChevronLeft size={20} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70 z-10"
            aria-label="Próxima imagem"
          >
            <FiChevronRight size={20} />
          </button>
        </>
      )}
    </div>
  );
};

const Viagens = () => {
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [promocaoModalOpen, setPromocaoModalOpen] = useState(false);
  const [viagemEdit, setViagemEdit] = useState(null);
  const [viagemPromocao, setViagemPromocao] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchViagens();
  }, []);

  const fetchViagens = async () => {
    try {
      const response = await api.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      toast.error('Erro ao carregar viagens');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta viagem?')) return;

    try {
      await api.delete(`/api/viagens/${id}`);
      toast.success('Viagem excluída com sucesso');
      fetchViagens();
    } catch (error) {
      toast.error('Erro ao excluir viagem');
    }
  };

  const handleEdit = (viagem) => {
    setViagemEdit(viagem);
    setModalOpen(true);
  };

  const handleAssentos = (viagemId) => {
    navigate(`/admin/assentos/${viagemId}`);
  };

  const handleToggleStatus = async (viagem) => {
    const estaAtiva = viagem.ativo === 1 || viagem.ativo === true;
    const acao = estaAtiva ? 'desativar' : 'ativar';
    if (!window.confirm(`Tem certeza que deseja ${acao} esta viagem?\n\n${estaAtiva ? 'A viagem será removida da página pública.' : 'A viagem voltará a aparecer na página pública.'}`)) return;

    try {
      await api.put(`/api/viagens/${viagem.id}/toggle-status`);
      toast.success(estaAtiva ? 'Viagem desativada com sucesso' : 'Viagem ativada com sucesso');
      fetchViagens();
    } catch (error) {
      toast.error('Erro ao alterar status da viagem');
    }
  };

  const handlePromocao = (viagem) => {
    setViagemPromocao(viagem);
    setPromocaoModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Viagens</h1>
          <p className="text-white mt-1">Gerencie as viagens disponíveis</p>
        </div>
        <button
          onClick={() => {
            setViagemEdit(null);
            setModalOpen(true);
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="mr-2" size={20} />
          Nova Viagem
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {viagens.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-white">Nenhuma viagem cadastrada</p>
            </div>
          ) : (
            viagens.map((viagem) => {
              const videoEmbedUrl = getYouTubeEmbedUrl(viagem.video_url);
              const todasImagens = viagem.imagens && Array.isArray(viagem.imagens) && viagem.imagens.length > 0 
                ? viagem.imagens.slice(0, 3).map(img => getImageUrl(img)) 
                : [];
              
              return (
              <div key={viagem.id} className="bg-sky-50 rounded-lg shadow overflow-hidden">
                <div className="h-64 bg-gray-200 overflow-hidden relative">
                  {todasImagens.length > 0 ? (
                    <ImageCarousel images={todasImagens} titulo={viagem.titulo} videoEmbedUrl={videoEmbedUrl} />
                  ) : videoEmbedUrl ? (
                    <iframe
                      src={videoEmbedUrl}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={viagem.titulo}
                    ></iframe>
                  ) : (
                    <div className="h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                      <FiMapPin className="text-primary-400" size={48} />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{viagem.titulo}</h3>
                    {(viagem.ativo === 0 || viagem.ativo === false) && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                        Desativada
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm text-gray-700 mb-4">
                    <div className="flex items-center">
                      <FiMapPin className="mr-2" size={16} />
                      {viagem.destino}
                    </div>
                    <div className="flex items-center">
                      <FiCalendar className="mr-2" size={16} />
                      {formatarData(viagem.data_ida)}
                    </div>
                    <div className="flex items-center">
                      <FiDollarSign className="mr-2" size={16} />
                      R$ {parseFloat(viagem.valor).toFixed(2)}
                    </div>
                    <div className="flex items-center">
                      <FiUsers className="mr-2" size={16} />
                      {viagem.vagas} vagas
                    </div>
                  </div>
                  <div className="space-y-2">
                    {/* Ativar/Desativar: controle anual – viagem concluída = desativar para não aparecer na página pública */}
                    <button
                      onClick={() => handleToggleStatus(viagem)}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                        (viagem.ativo === 1 || viagem.ativo === true)
                          ? 'bg-amber-100 border border-amber-400 text-amber-800 hover:bg-amber-200'
                          : 'bg-green-100 border border-green-500 text-green-800 hover:bg-green-200'
                      }`}
                      title={(viagem.ativo === 1 || viagem.ativo === true) ? 'Desativar: remove da página pública (viagem concluída)' : 'Ativar: exibe na página pública para clientes'}
                    >
                      {(viagem.ativo === 1 || viagem.ativo === true) ? (
                        <>
                          <FiXCircle size={18} />
                          Desativar (ocultar da página pública)
                        </>
                      ) : (
                        <>
                          <FiPower size={18} />
                          Ativar (exibir na página pública)
                        </>
                      )}
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAssentos(viagem.id)}
                        className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                      >
                        Assentos
                      </button>
                      <button
                        onClick={() => handlePromocao(viagem)}
                        className="px-4 py-2 bg-white border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 text-sm"
                        title="Enviar promoção para os 10 primeiros clientes"
                      >
                        <FiGift size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(viagem)}
                        className="px-4 py-2 bg-white border border-sky-200 rounded-lg hover:bg-sky-100 text-gray-700 text-sm"
                        title="Editar viagem"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(viagem.id)}
                        className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                        title="Excluir viagem"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                    {(viagem.ativo === 0 || viagem.ativo === false) && (
                      <p className="text-xs text-red-600 text-center">
                        Esta viagem não aparece na página pública. Ative para exibir novamente.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {modalOpen && (
        <ViagemModal
          viagem={viagemEdit}
          onClose={() => {
            setModalOpen(false);
            setViagemEdit(null);
          }}
          onSuccess={() => {
            setModalOpen(false);
            setViagemEdit(null);
            // Aguardar um pouco antes de recarregar para garantir que o servidor processou
            setTimeout(() => {
              fetchViagens();
            }, 500);
          }}
        />
      )}

      {promocaoModalOpen && (
        <PromocaoModal
          viagem={viagemPromocao}
          onClose={() => {
            setPromocaoModalOpen(false);
            setViagemPromocao(null);
          }}
        />
      )}
    </div>
  );
};

export default Viagens;
